import React, { useState, useEffect } from "react";
import { License, CreateLicenseData, EditLicenseData } from "../../store/types";
import { LICENSE_TYPES } from "../../types/licenseTypes";
import { useLicensesAPI } from "../../hooks/api/use-licensesAPI";
import { useCompaniesAPI } from "../../hooks/api/use-companiesAPI";
import { useProjectsAPI } from "../../hooks/api/use-projectsAPI";
import { useUsersAPI } from "../../hooks/api/use-usersAPI";
import { useGetQuery } from "../../store/slices/apiSlice";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "../../hooks/useMediaQuery";
import { validateLicenseField } from "../../hooks/validation";
import { formatDate } from "../../hooks/dateTimeConvertion";
import { getLicenseType } from "../../hooks/useLicenseType";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import CustomInputNumberIncrement from "../../components/CustomInput/CustomInputNumberIncrement";
import CustomDropdown from "../../components/CustomInput/CustomDropdown";
import CustomCalendar from "../../components/CustomInput/CustomCalendar";
import { Container } from "../../components/Container/Container";
import { StyledIconLine } from "../../components/StyledIcon/StyledIcon";
import { BodyText, DangerText, H2 } from "../../styles/Typography";
import {
  IconPlusBlack,
  IconCalendar,
  IconDocument,
  IconCompany,
  IconCalendarCheckFilled,
  IconCalendarXFilled,
  IconDocFilled,
  IconCompanyFilled,
  IconCaseFilled,
  IconCase,
  IconUserFilled,
  IconUser,
  IconArrowLeft,
} from "../../assets/icons/Icon";
import {
  Flex,
  ButtonLabel,
  GoBackButton,
  ButtonContainer,
  Spinner,
  Column,
  SmallColumn,
  Row,
  RowText,
} from "./CRUDPages.styles";

const initialFormData: CreateLicenseData = {
  numberOfLicenses: 1,
  type: "",
  projectId: "",
  organizationId: 0,
  startDate: null,
  expirationDate: null,
};

const initialErrors: Record<keyof CreateLicenseData, boolean> = {
  numberOfLicenses: false,
  type: false,
  projectId: false,
  organizationId: false,
  startDate: false,
  expirationDate: false,
};

const LicenseFormPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const licenseId = searchParams.get("id");
  const { companyId } = useParams<{ companyId: string }>();
  const { userId } = useParams<{ userId: string }>();
  const { projectId } = useParams<{ projectId: string }>();

  const isMobile = useMediaQuery("(max-width: 768px)");
  const navigate = useNavigate();
  const mode = searchParams.get("mode") || "create";

  const [loading, setLoading] = useState(false);
  const {
    licensesEP,
    handlePostLicense,
    handleActivateLicense,
    handleDeactivateLicense,
    handleUpdateLicense,
    handleUpdateLicenseUser,
    handleDeleteLicense,
    refetchData,
    toastRef,
  } = useLicensesAPI();
  const { companiesEP, companies } = useCompaniesAPI();
  const { projectsEP, projects } = useProjectsAPI();
  const { usersEP, users } = useUsersAPI();

  const [formData, setFormData] = useState<CreateLicenseData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateLicenseData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [addUserOpt, setAddUserOpt] = useState<{ label: string; value: any }[]>(
    []
  );
  const [isAvailableUsers, setIsAvailableUsers] = useState<boolean>(true);
  const [companiesOpt, setCompaniesOpt] = useState<
    { label: string; value: any }[]
  >([]);
  const [projectsOpt, setProjectsOpt] = useState<
    { label: string; value: any }[]
  >([]);
  const [isAddUser, setIsAddUser] = useState<boolean>(false);
  const [comment, setComment] = useState<string>("");

  const [enrichedLicense, setEnrichedLicense] = useState<
    | (License & {
        projectName?: string;
        companyName?: string;
        userName?: string;
        licenseType?: string;
      })
    | null
  >(null);

  const {
    data: license,
    refetch: refetchLicense,
    isLoading,
  } = useGetQuery(`${licensesEP}/${licenseId}`, {
    refetchOnMountOrArgChange: true,
    skip: !licenseId || !licensesEP,
  });

  const { data: user, refetch: refetchUser } = useGetQuery(
    `${usersEP}/${userId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !usersEP || !userId,
    }
  );

  const { data: company, refetch: refetchCompany } = useGetQuery(
    `${companiesEP}/${user?.organizationId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !companiesEP || !user,
    }
  );

  const { data: project, refetch: refetchProject } = useGetQuery(
    `${projectsEP}/${projectId}`,
    {
      refetchOnMountOrArgChange: true,
      skip: !projectsEP || !projectId,
    }
  );

  useEffect(() => {
    if (license && companies && projects && users) {
      const project = projects.find((p) => p.id === license.projectId);
      const company = companies.find((c) => c.id === license.organizationId);
      const user = users.find((u) => u.id === license.userId);
      setEnrichedLicense({
        ...license,
        projectName: project?.name ?? license.projectId,
        companyName: company?.name ?? license.organizationId,
        userName: user
          ? `${user.lastName ?? ""} ${user.name ?? ""} ${user.patronymic ?? ""}`
          : undefined,
        licenseType: getLicenseType(license.type),
      });
    } else {
      setEnrichedLicense(null);
    }
  }, [license, companies, projects, users]);

  useEffect(() => {
    if (companies) {
      const formatted = companies.map((c) => ({
        label: c.name,
        value: c.id,
      }));
      setCompaniesOpt(formatted);
    }
  }, [companies]);

  useEffect(() => {
    if (projects) {
      const formatted = projects.map((p) => ({
        label: p.name,
        value: p.id,
      }));
      setProjectsOpt(formatted);
    }
  }, [projects]);

  useEffect(() => {
    if (users && license?.organizationId) {
      const filtered = users
        .filter((u) => u.organizationId === license.organizationId)
        .map((u) => ({
          label: `${u.lastName ?? ""} ${u.name ?? ""} ${u.patronymic ?? ""}`,
          value: u.id,
        }));
      setAddUserOpt(filtered);
    }
  }, [users, license?.organizationId]);

  useEffect(() => {
    if (mode !== "create" && isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [license, isLoading]);

  useEffect(() => {
    if (mode !== "create" && licenseId && license) {
      const newData = {
        numberOfLicenses: license.numberOfLicenses || 0,
        type: license.type || "",
        projectId: license.projectId || "",
        organizationId: license.organizationId || 0,
        startDate: license.startDate ? new Date(license.startDate) : null,
        expirationDate: license.expirationDate
          ? new Date(license.expirationDate)
          : null,
      };
      setFormData(newData);
      setErrors(initialErrors);
      setComment(license.comments || "");
    } else {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setComment("");
    }
  }, [licenseId, mode, license]);

  useEffect(() => {
    if (isMobile === null) return;

    if (!isMobile) {
      if (companyId) navigate(`/comapnies/${companyId}`, { replace: true });
      if (userId) navigate(`/users/${userId}`, { replace: true });
      if (projectId) navigate(`/projects/${projectId}`, { replace: true });
      else navigate("/licenses", { replace: true });
    }
  }, [isMobile, navigate]);

  useEffect(() => {
    setIsAddUser(false);
    setSelectedUser(null);
  }, [licenseId, mode]);

  useEffect(() => {
    if (mode === "create") {
      if (companyId)
        setFormData((prev) => ({ ...prev, organizationId: Number(companyId) }));
      if (projectId) setFormData((prev) => ({ ...prev, projectId: projectId }));
      if (company)
        setFormData((prev) => ({
          ...prev,
          organizationId: Number(company.id),
        }));
    }
  }, [mode, companyId, company]);

  useEffect(() => {
    if (addUserOpt.length === 0) setIsAvailableUsers(false);
    else setIsAvailableUsers(true);
  }, [addUserOpt]);

  const handleFieldChange = (field: keyof CreateLicenseData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    if (mode !== "create") return true;

    const newErrors: Record<keyof CreateLicenseData, boolean> = {
      numberOfLicenses: !validateLicenseField(
        "numberOfLicenses",
        formData.numberOfLicenses
      ),
      type: !validateLicenseField("type", formData.type),
      projectId: !validateLicenseField("projectId", formData.projectId),
      organizationId: !validateLicenseField(
        "organizationId",
        formData.organizationId
      ),
      startDate: !validateLicenseField("startDate", formData.startDate),
      expirationDate: !validateLicenseField(
        "expirationDate",
        formData.expirationDate
      ),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateLicenseData) => {
    const value = formData[field];
    const isValid = validateLicenseField(field, value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (mode !== "delete") {
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }
    }

    setValidForm(true);
    setSubmitting(true);
    let success = false;

    try {
      if (mode === "edit" && isAddUser) {
        if (!selectedUser) {
          setValidForm(false);
          return;
        }
        success = await handleUpdateLicenseUser(license!.id, selectedUser);
      } else {
        switch (mode) {
          case "create":
            const addResult = await handlePostLicense(formData);
            success = addResult.success;
            if (success && userId && addResult.licenses) {
              for (const license of addResult.licenses) {
                await handleUpdateLicenseUser(license.id, userId);
              }
            }
            break;
          case "edit": {
            const editData: EditLicenseData = {
              startDate: formData.startDate,
              expirationDate: formData.expirationDate,
              comments: comment,
            };
            success = await handleUpdateLicense(editData, license!.id);
            break;
          }
          case "delete":
            success = await handleDeleteLicense(license!);
            break;
          case "enable":
            success = await handleActivateLicense(license!.id);
            break;
          case "disable":
            success = await handleDeactivateLicense(license!.id);
            break;
          case "addUser":
            if (!selectedUser) {
              return;
            }
            success = await handleUpdateLicenseUser(license!.id, selectedUser);
            break;
          default:
            success = false;
        }
      }

      if (success) {
        refetchData();
        setTimeout(() => {
          navigate(-1);
        }, 1500);
      } else return false;
    } finally {
      setSubmitting(false);
    }
  };

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новая лицензия";
      case "edit":
        return "Редактировать лицензию";
      case "delete":
        return "Удалить лицензию";
      case "enable":
        return "Активировать лицензию";
      case "disable":
        return "Деактивировать лицензию";
      case "addUser":
        return "Добавить пользователя";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const getButtonLabel = (type: string) => {
    switch (type) {
      case "create":
        return "Создать";
      case "edit":
        return "Сохранить";
      case "delete":
        return "Удалить";
      case "enable":
        return "Активировать";
      case "disable":
        return "Деактивировать";
      case "addUser":
        return "Добавить";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const usersDropdownForm = (
    <>
      {isAvailableUsers ? (
        <CustomDropdown
          value={selectedUser}
          onChange={(val) => setSelectedUser(val)}
          options={addUserOpt}
          placeholder="Пользователь"
          icon={<IconUser size={20} />}
          filter
        />
      ) : (
        <BodyText>Нет пользователей в этой компании</BodyText>
      )}
    </>
  );

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {mode === "create" ? (
        <>
          <CustomInputNumberIncrement
            value={formData.numberOfLicenses}
            setValue={(val) => handleFieldChange("numberOfLicenses", val)}
            onBlur={() => handleBlur("numberOfLicenses")}
            placeholder="Количество лицензий"
            invalid={errors.numberOfLicenses}
          />
          <CustomDropdown
            value={formData.type}
            onChange={(val) => handleFieldChange("type", val)}
            onBlur={() => handleBlur("type")}
            options={LICENSE_TYPES}
            placeholder="Тип лицензии"
            icon={<IconDocument size={20} />}
            invalid={errors.type}
          />
          {projectId || project ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.projectId}
              onChange={(val) => handleFieldChange("projectId", val)}
              onBlur={() => handleBlur("projectId")}
              options={projectsOpt}
              placeholder="Проект"
              icon={<IconCase size={20} />}
              invalid={errors.projectId}
              filter
            />
          )}
          {companyId || company ? (
            <></>
          ) : (
            <CustomDropdown
              value={formData.organizationId}
              onChange={(val) => handleFieldChange("organizationId", val)}
              onBlur={() => handleBlur("organizationId")}
              options={companiesOpt}
              placeholder="Компания"
              icon={<IconCompany size={20} />}
              invalid={errors.organizationId}
              filter
            />
          )}
          <Row>
            <CustomCalendar
              key="start-date"
              value={formData.startDate}
              setValue={(date) => handleFieldChange("startDate", date)}
              onBlur={() => handleBlur("startDate")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startDate}
            />
            <RowText>—</RowText>
            <CustomCalendar
              key="expiration-date"
              value={formData.expirationDate}
              setValue={(date) => handleFieldChange("expirationDate", date)}
              onBlur={() => handleBlur("expirationDate")}
              placeholder="Дата окончания"
              icon={<IconCalendar size={20} />}
              invalid={errors.expirationDate}
            />
          </Row>
        </>
      ) : license ? (
        <>
          <SmallColumn>
            <StyledIconLine
              icon={<IconCaseFilled size={20} />}
              text={enrichedLicense?.projectName ?? "Нет данных"}
            />
            <StyledIconLine
              icon={<IconCompanyFilled size={20} />}
              text={`${enrichedLicense?.companyName ?? "Нет данных"}`}
            />
            {enrichedLicense?.userName &&
            enrichedLicense?.licenseType !== "Сессионная" ? (
              <StyledIconLine
                icon={<IconUserFilled size={20} />}
                text={`${
                  enrichedLicense?.userName
                    ? enrichedLicense?.userName === "—"
                      ? "Не добавлен"
                      : enrichedLicense?.userName
                    : "Нет добавлен"
                }`}
              />
            ) : (
              <></>
            )}
          </SmallColumn>
          <Row>
            <CustomCalendar
              key="start-date-license"
              value={formData.startDate}
              setValue={(date) => handleFieldChange("startDate", date)}
              onBlur={() => handleBlur("startDate")}
              placeholder="Дата начала"
              icon={<IconCalendar size={20} />}
              invalid={errors.startDate}
            />
            <RowText>—</RowText>
            <CustomCalendar
              key="expiration-date-license"
              value={formData.expirationDate}
              setValue={(date) => handleFieldChange("expirationDate", date)}
              onBlur={() => handleBlur("expirationDate")}
              placeholder="Дата окончания"
              icon={<IconCalendar size={20} />}
              invalid={errors.expirationDate}
            />
          </Row>
          <InputTextarea
            autoResize
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={5}
            cols={30}
            placeholder="Комментарий"
          />
          {license?.userId ? (
            <></>
          ) : (
            <>
              <Button
                outlined
                severity="secondary"
                onClick={() => setIsAddUser(true)}
                visible={!isAddUser}
              >
                <IconPlusBlack size={20} />
                <BodyText style={{ marginLeft: "5px" }}>
                  Добавить пользователя
                </BodyText>
              </Button>
              {isAddUser ? usersDropdownForm : <></>}
            </>
          )}
        </>
      ) : (
        <BodyText>Загрузка...</BodyText>
      )}
    </Column>
  );

  const addUserForm = (
    <Column>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={enrichedLicense?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${enrichedLicense?.companyName ?? "Нет данных"}`}
      />
      {usersDropdownForm}
    </Column>
  );

  const infoForm = (
    <SmallColumn>
      <StyledIconLine
        icon={<IconCaseFilled size={20} />}
        text={enrichedLicense?.projectName ?? "Нет данных"}
      />
      <StyledIconLine
        icon={<IconCompanyFilled size={20} />}
        text={`${enrichedLicense?.companyName ?? "Нет данных"}`}
      />
      {enrichedLicense?.userName &&
      enrichedLicense?.licenseType !== "Сессионная" ? (
        <StyledIconLine
          icon={<IconUserFilled size={20} />}
          text={`${
            enrichedLicense?.userName
              ? enrichedLicense?.userName === "—"
                ? "Не добавлен"
                : enrichedLicense?.userName
              : "Нет добавлен"
          }`}
        />
      ) : (
        <></>
      )}
      <StyledIconLine
        icon={<IconDocFilled size={20} />}
        text={
          enrichedLicense?.licenseType
            ? `${enrichedLicense?.licenseType} лицензия`
            : "Нет данных"
        }
      />
      <StyledIconLine
        icon={<IconCalendarCheckFilled size={20} />}
        text={`Дата начала: ${
          license?.startDate
            ? formatDate(license.startDate.toString())
            : "Нет данных"
        }`}
      />
      <StyledIconLine
        icon={<IconCalendarXFilled size={20} />}
        text={`Дата завершения: ${
          license?.expirationDate
            ? formatDate(license.expirationDate.toString())
            : "Нет данных"
        }`}
      />
    </SmallColumn>
  );

  const deleteForm = (
    <Column>
      <DangerText>Вы уверены, что хотите удалить данную лицензию?</DangerText>
      {infoForm}
    </Column>
  );

  const enableForm = (
    <Column>
      <BodyText>Активировать данную лицензию?</BodyText>
      {infoForm}
    </Column>
  );

  const disableForm = (
    <Column>
      <DangerText>
        Вы уверены, что хотите деактивировать данную лицензию?
      </DangerText>
      {infoForm}
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      case "enable":
        return enableForm;
      case "disable":
        return disableForm;
      case "addUser":
        return addUserForm;
      default:
        return <></>;
    }
  };

  if (loading)
    return (
      <Container>
        <Spinner>
          <ProgressSpinner />
          <p>Загрузка...</p>
        </Spinner>
      </Container>
    );

  return (
    <Container>
      <Toast ref={toastRef} position="bottom-center" />
      <H2>{getHeader(mode)}</H2>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      {getContent(mode)}
      <Flex>
        <GoBackButton>
          <Button outlined onClick={() => navigate(-1)}>
            <ButtonLabel>
              <IconArrowLeft size={16} />
              Назад
            </ButtonLabel>
          </Button>
        </GoBackButton>
        <ButtonContainer>
          <Button
            label={getButtonLabel(mode)}
            onClick={handleSubmit}
            loading={submitting}
          />
        </ButtonContainer>
      </Flex>
    </Container>
  );
};

export default LicenseFormPage;
